import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';

import {
    buscarAreasConfiguracion,
    buscarOrganizadoresConfiguracion,
    buscarUbicacionesConfiguracion
} from '../../../actions/configuracion';

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const toOption = (item) => ({ value: item?._id || '', label: item?.nombre || 'Sin definir' });

const filaVacia = (cantidad) => ({
    rowId: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    area: null,
    organizador: null,
    ubicacion: null,
    cantidad
});

const DevolverProductoVentaModal = ({ show, onHide, item, onConfirm }) => {
    const dispatch = useDispatch();

    const { areas } = useSelector(state => state.configuracion);

    const disponible = Number(item?.cantidad || 0);

    const [filas, setFilas] = useState([]);
    const [organizadoresPorFila, setOrganizadoresPorFila] = useState({});
    const [ubicacionesPorFila, setUbicacionesPorFila] = useState({});

    const opcionesArea = useMemo(() => (areas?.listado || []).map(toOption), [areas]);

    useEffect(() => {
        if (!show) {
            setFilas([]);
            setOrganizadoresPorFila({});
            setUbicacionesPorFila({});
            return;
        }

        dispatch(buscarAreasConfiguracion());
        setFilas([filaVacia(disponible)]);
    }, [show, dispatch, disponible]);

    const asignado = filas.reduce((acc, f) => acc + Number(f.cantidad || 0), 0);
    const faltaArea = filas.some((f) => Number(f.cantidad || 0) > 0 && !f.area?.value);
    const puedeConfirmar = asignado > 0 && asignado <= disponible && !faltaArea;

    const actualizarFila = (rowId, cambios) => {
        setFilas((prev) => prev.map((f) => f.rowId === rowId ? { ...f, ...cambios } : f));
    };

    const handleAgregarFila = () => {
        setFilas((prev) => [...prev, filaVacia(0)]);
    };

    const handleEliminarFila = (rowId) => {
        setFilas((prev) => prev.filter((f) => f.rowId !== rowId));
    };

    const handleSelectArea = async (rowId, opt) => {
        actualizarFila(rowId, { area: opt || null, organizador: null, ubicacion: null });
        setOrganizadoresPorFila((prev) => ({ ...prev, [rowId]: [] }));
        setUbicacionesPorFila((prev) => ({ ...prev, [rowId]: [] }));

        if (!opt?.value) return;

        const res = await dispatch(buscarOrganizadoresConfiguracion(opt.value));
        setOrganizadoresPorFila((prev) => ({ ...prev, [rowId]: res?.organizadores || [] }));
    };

    const handleSelectOrganizador = async (rowId, opt) => {
        actualizarFila(rowId, { organizador: opt || null, ubicacion: null });
        setUbicacionesPorFila((prev) => ({ ...prev, [rowId]: [] }));

        if (!opt?.value) return;

        const res = await dispatch(buscarUbicacionesConfiguracion(opt.value));
        setUbicacionesPorFila((prev) => ({ ...prev, [rowId]: res?.ubicaciones || [] }));
    };

    const handleSelectUbicacion = (rowId, opt) => {
        actualizarFila(rowId, { ubicacion: opt || null });
    };

    const handleChangeCantidad = (rowId, value) => {
        const v = value === '' ? '' : Math.max(0, Number(value));
        actualizarFila(rowId, { cantidad: v });
    };

    const handleConfirmar = () => {
        if (!puedeConfirmar) return;

        const destinos = filas
            .filter((f) => Number(f.cantidad || 0) > 0)
            .map((f) => ({
                area: { _id: f.area.value, nombre: f.area.label },
                organizador: f.organizador ? { _id: f.organizador.value, nombre: f.organizador.label } : null,
                ubicacion: f.ubicacion ? { _id: f.ubicacion.value, nombre: f.ubicacion.label } : null,
                cantidad: Number(f.cantidad)
            }));

        onConfirm(destinos);
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Devolver producto</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="mb-3">
                    <div className="fw-semibold">{item?.productoNombre}</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                        Cantidad disponible para devolver: {formatNumber(disponible)} {item?.unidadNombre || ''}
                    </div>
                </div>

                <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                    Indica a qué ubicación de inventario regresa el producto. Puedes dividir la cantidad en varias ubicaciones.
                </div>

                <div className="table-responsive">
                    <Table bordered hover className="mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: 90 }} className="text-center">Cantidad</th>
                                <th>Ubicación destino</th>
                                <th style={{ width: 40 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {filas.map((f) => (
                                <tr key={f.rowId}>
                                    <td className="align-middle">
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            value={f.cantidad}
                                            min={0}
                                            onChange={(e) => handleChangeCantidad(f.rowId, e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <div style={{ minWidth: 130, maxWidth: 160 }}>
                                                <Select
                                                    options={opcionesArea}
                                                    value={f.area}
                                                    onChange={(opt) => handleSelectArea(f.rowId, opt)}
                                                    isClearable
                                                    placeholder="Área"
                                                    noOptionsMessage={() => 'Sin resultados'}
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </div>
                                            <div style={{ minWidth: 130, maxWidth: 160 }}>
                                                <Select
                                                    options={(organizadoresPorFila[f.rowId] || []).map(toOption)}
                                                    value={f.organizador}
                                                    onChange={(opt) => handleSelectOrganizador(f.rowId, opt)}
                                                    isClearable
                                                    isDisabled={!f.area}
                                                    placeholder="Organizador"
                                                    noOptionsMessage={() => 'Sin resultados'}
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </div>
                                            <div style={{ minWidth: 130, maxWidth: 160 }}>
                                                <Select
                                                    options={(ubicacionesPorFila[f.rowId] || []).map(toOption)}
                                                    value={f.ubicacion}
                                                    onChange={(opt) => handleSelectUbicacion(f.rowId, opt)}
                                                    isClearable
                                                    isDisabled={!f.organizador}
                                                    placeholder="Ubicación"
                                                    noOptionsMessage={() => 'Sin resultados'}
                                                    menuPortalTarget={document.body}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-center align-middle">
                                        {filas.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link px-1"
                                                title="Eliminar fila"
                                                onClick={() => handleEliminarFila(f.rowId)}
                                            >
                                                <i className="fas fa-trash text-danger" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                <div className="d-flex align-items-center justify-content-between mt-2">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleAgregarFila}>
                        <i className="feather icon-plus" /> Dividir en otra ubicación
                    </button>
                    <div className={puedeConfirmar ? 'text-success' : 'text-muted'} style={{ fontSize: 12 }}>
                        Asignado: {formatNumber(asignado)} / {formatNumber(disponible)}
                    </div>
                </div>

                {!puedeConfirmar && (
                    <div className="mt-3">
                        <Badge bg="danger">Asigna una ubicación a cada cantidad sin exceder lo disponible</Badge>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button variant="success" onClick={handleConfirmar} disabled={!puedeConfirmar}>
                    Confirmar devolución
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DevolverProductoVentaModal;
